import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationEnd } from '@angular/router';
import { AbilityBuilder, createMongoAbility, MongoAbility } from '@casl/ability';
import { BehaviorSubject, filter } from 'rxjs';

export type ModulesTypes =
  | 'Scheduler'
  | 'Warranty'
  | 'HomeBuilding'
  | 'Sales'
  | 'File Management'
  | 'DSM'
  | 'Administration';

export type SalesRoles = 'SuperAdmin' | 'Admin' | 'Sales' | 'LegalUser' | 'ReportsUser';

export type SalesSubjects =
  | 'user-setup'
  | 'prospects'
  | 'brokerage'
  | 'realtors'
  | 'deals'
  | 'spec-lots'
  | 'calendar'
  | 'sale-map'
  | 'project-email-templates'
  | 'sales-reports'
  | 'deposit-defaults';

export type SalesActions =
  | 'read-sales-user-setup'
  | 'read-sales-prospects'
  | 'update-sales-prospects'
  | 'read-sales-brokerage'
  | 'update-sales-brokerage'
  | 'read-sales-realtors'
  | 'update-sales-realtors'
  | 'read-sales-deals'
  | 'write-sales-deals'
  | 'read-sales-spec-lots'
  | 'read-sales-calendar'
  | 'read-sale-map'
  | 'send-welcome-email'
  | 'mark-deal-closed'
  | 'cancel-locked-addendum'
  | 'edit-locked-addendum'
  | 'edit-locked-addendum-location'
  | 'edit-under-contract-price'
  | 'read-project-email-templates'
  | 'read-sales-reports'
  | 'read-sales-deposit-defaults';

// | 'read-sales-dashboard'
// | 'read-sales-agencies'
// | 'read-sales-office'
// | 'update-sales-office'

export type SchedulerRoles = 'SuperAdmin' | 'Admin' | 'SiteSuper' | 'Trade';
export type SchedulerSubjects =
  | 'scheduler-user-setup'
  | 'tasks-import'
  | 'create-schedule'
  | 'create-project-schedule'
  | 'schedule-lots'
  | 'tasks-review'
  | 'schedules-reports'
  | 'ounit-schedules-reports'
  | 'projects-schedules-reports'
  | 'lots-schedules-reports';
export type SchedulerActions =
  // access actions
  | 'access-user-setup'
  | 'access-tasks-import'
  | 'access-create-schedule'
  | 'access-create-project-schedule'
  | 'access-schedule-lots'
  | 'access-tasks-review'
  | 'access-schedules-reports'
  | 'access-ounit-schedules-reports'
  | 'access-projects-schedules-reports'
  | 'access-lots-schedules-reports'
  // user-setup actions
  | 'read-user-setup'
  | 'update-user-setup'
  // tasks-import actions
  | 'read-tasks'
  | 'create-tasks'
  | 'update-tasks'
  // create-schedule actions
  | 'read-schedule'
  | 'create-schedule'
  | 'update-schedule'
  | 'replicate-schedule'
  // create-project-schedule actions
  | 'generate-project-schedule'
  // schedule-lots actions
  | 'read-schedule-lots-tasks'
  | 'update-schedule-lots-tasks'
  // tasks-review actions
  | 'read-tasks-review'
  | 'update-tasks-review'
  // ounit-schedules-reports actions
  | 'read-ounit-schedules-reports'
  // projects-schedules-reports actions
  | 'read-projects-schedules-reports'
  // lots-schedules-reports actions
  | 'read-lots-schedules-reports';

export type WarrantyRoles = 'Admin' | 'Coordinator' | 'Assistant' | 'ServiceTechnician' | 'SuperAdmin';
export type WarrantySubjects =
  | 'work-order'
  | 'scheduler'
  | 'types-setup'
  | 'locations-setup'
  | 'categories-setup'
  | 'deficiencies-setup'
  | 'options-setup'
  | 'xml-import'
  | 'reports'
  | 'search'
  | 'user-setup';
export type WarrantyActions =
  // general actions
  | 'read-projects'
  | 'read-lots'
  | 'read-trades'
  | 'read-technicians'
  | 'read-deficiencies'
  | 'read-addresses'

  // warranty work order actions
  | 'create-work-order'
  | 'update-work-order'
  | 'read-work-order'
  | 'read-options'
  | 'generate-pdf'
  | 'assign-work-order'
  | 'bulk-complete-work-order'

  // warranty scheduler actions
  | 'read-warranty-scheduler'
  | 'update-warranty-scheduler-assistant'
  | 'update-warranty-scheduler-technician'

  // warranty search actions
  | 'read-warranty-search'
  | 'update-warranty-search'

  // user setup actions
  | 'read-user-setup'
  | 'update-user-setup'

  // warranty types setup actions
  | 'read-warranty-types'
  | 'create-warranty-types'
  | 'update-warranty-types'

  // warranty locations setup actions
  | 'read-warranty-locations'
  | 'create-warranty-locations'
  | 'update-warranty-locations'

  // warranty categories setup actions
  | 'read-warranty-categories'
  | 'create-warranty-categories'
  | 'update-warranty-categories'

  // warranty deficiencies setup actions
  | 'read-warranty-deficiencies'
  | 'create-warranty-deficiencies'
  | 'update-warranty-deficiencies'

  // read options setup actions
  | 'read-warranty-options'
  | 'create-warranty-options'
  | 'update-warranty-options'
  | 'delete-warranty-options'

  // xml import actions
  | 'read-xml-import'

  // warranty reports actions
  | 'read-warranty-reports';

export type HomeBuildingRoles = 'SuperAdmin';
export type HomeBuildingActions = 'create' | 'update' | 'delete' | 'read';
export type HomeBuildingSubjects = 'Task' | 'User';

export type DsmRoles = 'SuperAdmin' | 'Admin' | 'Décor Consultant' | 'Décor Receptionist';
export type DsmActions =
  | 'create'
  | 'update'
  | 'delete'
  | 'read'
  | 'publish'
  | 'bulk-delete'
  | 'toggle-returned'
  | 'dsm-read-aps'
  | 'dsm-cancel-locked-addendum'
  | 'dsm-approve-addendum'
  | 'dsm-edit-locked-addendum'
  | 'dsm-edit-locked-addendum-location'
  | 'dsm-rerun-room-replacement-rules'
  | 'dsm-view-pending-location-rules'
  | 'apply-cutoff'
  | 'read-dsm-user-setup'
  | 'read-dsm-selection-summary-report'
  | 'read-dsm-commission-report'
  | 'manage-dsm-location-rules'
  | 'manage-dsm-packages'
  | 'manage-dsm-catalog'
  | 'read-dsm-options-catalog'
  | 'manage-dsm-appointment-scenarios'
  | 'dsm-read-appointments'
  | 'dsm-view-all-appointments'
  | 'dsm-schedule-appointments'
  | 'dsm-flag-own-appointment';
export type DsmSubjects =
  | 'product-catalog'
  | 'category'
  | 'template'
  | 'deposit'
  | 'aps'
  | 'aps-addendum'
  | 'user-setup'
  | 'reports'
  | 'location-rules'
  | 'packages'
  | 'appointment-scenarios'
  | 'appointments'
  | 'options-catalog';

export type FileManagementRoles = 'SuperAdmin' | 'Admin' | 'SalesStaff' | 'SiteSuper' | 'Staff' | 'Trade';

export type FileManagementSubjects = 'folder' | 'template' | 'starred' | 'deleted-files' | 'admin-console' | 'files';

export type FileManagementActions =
  // folder actions
  | 'create-folder'
  | 'create-folder-at-root'
  // template actions
  | 'view-templates'
  | 'read-templates'
  | 'create-from-template'
  // sidebar section visibility actions
  | 'view-starred'
  | 'view-deleted-files'
  // admin console actions
  | 'access-admin-console'
  // files/folders browser actions
  | 'view-files'
  | 'manage-files'
  | 'upload-files';

export type AdministrationRoles = 'SuperAdmin';
export type AdministrationActions = 'any';
export type AdministrationSubjects = 'any';

type ActionsMap = {
  Scheduler: SchedulerActions;
  Warranty: WarrantyActions;
  HomeBuilding: HomeBuildingActions;
  Sales: SalesActions;
  'File Management': FileManagementActions;
  DSM: DsmActions;
  Administration: AdministrationActions;
};

type SubjectsMap = {
  Scheduler: SchedulerSubjects;
  Warranty: WarrantySubjects;
  HomeBuilding: HomeBuildingSubjects;
  Sales: SalesSubjects;
  'File Management': FileManagementSubjects;
  DSM: DsmSubjects;
  Administration: AdministrationSubjects;
};

type RolesMap = {
  Scheduler: SchedulerRoles;
  Warranty: WarrantyRoles;
  HomeBuilding: HomeBuildingRoles;
  Sales: SalesRoles;
  'File Management': FileManagementRoles;
  DSM: DsmRoles;
  Administration: AdministrationRoles;
};

export type Rules = {
  [M in ModulesTypes]: {
    [R in RolesMap[M]]?: {
      [S in SubjectsMap[M]]?: ActionsMap[M][];
    };
  };
};

export type AppAbility = MongoAbility<
  | [SchedulerActions, SchedulerSubjects]
  | [WarrantyActions, WarrantySubjects]
  | [SalesActions, SalesSubjects]
  | [FileManagementActions, FileManagementSubjects]
  | [DsmActions, DsmSubjects]
>;

@Injectable({
  providedIn: 'root'
})
export class AbilityService {
  router = inject(Router);
  private currentRouteSubject = new BehaviorSubject<string>(this.router.url);
  currentRoute$ = this.currentRouteSubject.asObservable();

  constructor() {
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.currentRouteSubject.next(event.urlAfterRedirects);
    });
  }
  private readonly rules: Rules = {
    Scheduler: {
      SuperAdmin: {
        'scheduler-user-setup': ['access-user-setup', 'read-user-setup', 'update-user-setup'],
        'tasks-import': ['access-tasks-import', 'read-tasks', 'create-tasks', 'update-tasks'],
        'create-schedule': [
          'access-create-schedule',
          'read-schedule',
          'create-schedule',
          'update-schedule',
          'replicate-schedule'
        ],
        'create-project-schedule': ['access-create-project-schedule', 'generate-project-schedule'],
        'schedule-lots': ['access-schedule-lots', 'read-schedule-lots-tasks', 'update-schedule-lots-tasks'],
        'tasks-review': ['access-tasks-review', 'read-tasks-review', 'update-tasks-review'],
        'schedules-reports': ['access-schedules-reports'],
        'ounit-schedules-reports': ['access-ounit-schedules-reports', 'read-ounit-schedules-reports'],
        'projects-schedules-reports': ['access-projects-schedules-reports', 'read-projects-schedules-reports'],
        'lots-schedules-reports': ['access-lots-schedules-reports', 'read-lots-schedules-reports']
      },
      Admin: {
        'scheduler-user-setup': ['access-user-setup', 'read-user-setup', 'update-user-setup'],
        'create-project-schedule': ['access-create-project-schedule', 'generate-project-schedule'],
        'create-schedule': [
          'access-create-schedule',
          'read-schedule',
          'create-schedule',
          'update-schedule',
          'replicate-schedule'
        ],
        'schedule-lots': ['access-schedule-lots', 'read-schedule-lots-tasks', 'update-schedule-lots-tasks'],
        'tasks-review': ['access-tasks-review', 'read-tasks-review', 'update-tasks-review'],
        'schedules-reports': ['access-schedules-reports'],
        'ounit-schedules-reports': ['access-ounit-schedules-reports', 'read-ounit-schedules-reports'],
        'projects-schedules-reports': ['access-projects-schedules-reports', 'read-projects-schedules-reports'],
        'lots-schedules-reports': ['access-lots-schedules-reports', 'read-lots-schedules-reports']
      },
      SiteSuper: {
        'schedule-lots': ['access-schedule-lots', 'read-schedule-lots-tasks', 'update-schedule-lots-tasks'],
        'tasks-review': ['access-tasks-review', 'read-tasks-review', 'update-tasks-review']
      },
      Trade: {
        'tasks-review': ['access-tasks-review', 'read-tasks-review', 'update-tasks-review']
      }
    },
    Warranty: {
      Coordinator: {
        'work-order': [
          'create-work-order',
          'read-work-order',
          'read-projects',
          'read-lots',
          'read-warranty-types',
          'update-work-order',
          'read-options',
          'generate-pdf'
        ]
      },
      Assistant: {
        scheduler: ['read-warranty-scheduler', 'update-warranty-scheduler-assistant'],
        'work-order': [
          'create-work-order',
          'update-work-order',
          'read-work-order',
          'read-options',
          'generate-pdf',
          'assign-work-order',
          'bulk-complete-work-order'
        ],
        search: ['read-warranty-search', 'update-warranty-search']
      },
      ServiceTechnician: {
        scheduler: ['read-warranty-scheduler', 'update-warranty-scheduler-technician']
      },
      Admin: {
        'user-setup': ['read-user-setup', 'update-user-setup'],
        'types-setup': ['read-warranty-types', 'create-warranty-types', 'update-warranty-types'],
        'locations-setup': ['read-warranty-locations', 'create-warranty-locations', 'update-warranty-locations'],
        'categories-setup': ['read-warranty-categories', 'create-warranty-categories', 'update-warranty-categories'],
        'deficiencies-setup': [
          'read-warranty-deficiencies',
          'create-warranty-deficiencies',
          'update-warranty-deficiencies'
        ],
        'options-setup': [
          'read-warranty-options',
          'create-warranty-options',
          'update-warranty-options',
          'delete-warranty-options'
        ],
        'xml-import': ['read-xml-import'],
        reports: ['read-warranty-reports']
      },
      SuperAdmin: {
        'user-setup': ['read-user-setup', 'update-user-setup'],
        'work-order': [
          'create-work-order',
          'update-work-order',
          'read-work-order',
          'read-options',
          'generate-pdf',
          'assign-work-order',
          'bulk-complete-work-order'
        ],
        scheduler: [
          'read-warranty-scheduler',
          'update-warranty-scheduler-assistant',
          'update-warranty-scheduler-technician'
        ],
        'types-setup': ['read-warranty-types', 'create-warranty-types', 'update-warranty-types'],
        'locations-setup': ['read-warranty-locations', 'create-warranty-locations', 'update-warranty-locations'],
        'categories-setup': ['read-warranty-categories', 'create-warranty-categories', 'update-warranty-categories'],
        'deficiencies-setup': [
          'read-warranty-deficiencies',
          'create-warranty-deficiencies',
          'update-warranty-deficiencies'
        ],
        'options-setup': [
          'read-warranty-options',
          'create-warranty-options',
          'update-warranty-options',
          'delete-warranty-options'
        ],
        'xml-import': ['read-xml-import'],
        reports: ['read-warranty-reports'],
        search: ['read-warranty-search', 'update-warranty-search']
      }
    },
    HomeBuilding: {
      SuperAdmin: {
        Task: ['read', 'create', 'update', 'delete'],
        User: ['read', 'create', 'update', 'delete']
      }
    },
    Sales: {
      SuperAdmin: {
        'user-setup': ['read-sales-user-setup'],
        prospects: ['read-sales-prospects', 'update-sales-prospects'],
        brokerage: ['read-sales-brokerage', 'update-sales-brokerage'],
        realtors: ['read-sales-realtors', 'update-sales-realtors'],
        deals: [
          'read-sales-deals',
          'write-sales-deals',
          'send-welcome-email',
          'mark-deal-closed',
          'cancel-locked-addendum',
          'edit-locked-addendum',
          'edit-locked-addendum-location',
          'edit-under-contract-price'
        ],
        'spec-lots': ['read-sales-spec-lots'],
        calendar: ['read-sales-calendar', 'mark-deal-closed'],
        'sale-map': ['read-sale-map'],
        'project-email-templates': ['read-project-email-templates'],
        'sales-reports': ['read-sales-reports'],
        'deposit-defaults': ['read-sales-deposit-defaults']
      },
      Admin: {
        'user-setup': ['read-sales-user-setup'],
        prospects: ['read-sales-prospects', 'update-sales-prospects'],
        brokerage: ['read-sales-brokerage', 'update-sales-brokerage'],
        realtors: ['read-sales-realtors', 'update-sales-realtors'],
        deals: [
          'read-sales-deals',
          'write-sales-deals',
          'send-welcome-email',
          'cancel-locked-addendum',
          'edit-locked-addendum-location',
          'edit-under-contract-price'
        ],
        'spec-lots': ['read-sales-spec-lots'],
        calendar: ['read-sales-calendar'],
        'sale-map': ['read-sale-map'],
        'project-email-templates': ['read-project-email-templates'],
        'sales-reports': ['read-sales-reports']
      },
      Sales: {
        'user-setup': [],
        prospects: ['read-sales-prospects', 'update-sales-prospects'],
        brokerage: ['read-sales-brokerage'],
        realtors: ['read-sales-realtors'],
        deals: ['read-sales-deals', 'write-sales-deals'],
        'spec-lots': ['read-sales-spec-lots'],
        calendar: ['read-sales-calendar'],
        'sale-map': ['read-sale-map']
      },
      LegalUser: {
        'user-setup': ['read-sales-user-setup'],
        prospects: ['read-sales-prospects', 'update-sales-prospects'],
        brokerage: ['read-sales-brokerage', 'update-sales-brokerage'],
        realtors: ['read-sales-realtors', 'update-sales-realtors'],
        deals: ['read-sales-deals', 'write-sales-deals', 'send-welcome-email', 'mark-deal-closed'],
        'spec-lots': ['read-sales-spec-lots'],
        calendar: ['read-sales-calendar', 'mark-deal-closed'],
        'sale-map': ['read-sale-map'],
        'project-email-templates': ['read-project-email-templates'],
        'sales-reports': ['read-sales-reports']
      },
      ReportsUser: {
        'sales-reports': ['read-sales-reports']
      }
    },
    'File Management': {
      SuperAdmin: {
        folder: ['create-folder', 'create-folder-at-root'],
        template: ['view-templates', 'read-templates', 'create-from-template'],
        starred: ['view-starred'],
        'deleted-files': ['view-deleted-files'],
        'admin-console': ['access-admin-console'],
        files: ['view-files', 'manage-files', 'upload-files']
      },
      Admin: {
        folder: ['create-folder', 'create-folder-at-root'],
        template: ['view-templates', 'read-templates', 'create-from-template'],
        starred: ['view-starred'],
        'deleted-files': ['view-deleted-files'],
        'admin-console': ['access-admin-console'],
        files: ['view-files', 'manage-files', 'upload-files']
      },
      SalesStaff: {
        folder: ['create-folder'],
        starred: ['view-starred'],
        files: ['view-files', 'manage-files']
      },
      SiteSuper: {
        folder: ['create-folder'],
        starred: ['view-starred'],
        files: ['view-files']
      },
      Staff: {
        starred: ['view-starred']
      },
      Trade: {}
    },
    DSM: {
      SuperAdmin: {
        'product-catalog': ['read', 'create', 'update', 'delete', 'publish', 'manage-dsm-catalog'],
        'options-catalog': ['read-dsm-options-catalog'],
        category: ['read', 'create', 'update', 'delete'],
        template: ['read', 'create', 'update', 'delete'],
        deposit: ['bulk-delete', 'toggle-returned'],
        aps: [
          'dsm-read-aps',
          'dsm-cancel-locked-addendum',
          'dsm-approve-addendum',
          'dsm-edit-locked-addendum',
          'dsm-edit-locked-addendum-location',
          'dsm-rerun-room-replacement-rules',
          'dsm-view-pending-location-rules',
          'create',
          'update',
          'delete'
        ],
        'aps-addendum': ['apply-cutoff'],
        'user-setup': ['read-dsm-user-setup'],
        reports: ['read-dsm-selection-summary-report', 'read-dsm-commission-report'],
        'location-rules': ['manage-dsm-location-rules'],
        packages: ['manage-dsm-packages'],
        'appointment-scenarios': ['manage-dsm-appointment-scenarios'],
        appointments: ['dsm-read-appointments', 'dsm-view-all-appointments', 'dsm-schedule-appointments']
      },
      Admin: {
        'product-catalog': ['read', 'manage-dsm-catalog'],
        'options-catalog': ['read-dsm-options-catalog'],
        category: ['read'],
        template: ['read'],
        aps: [
          'dsm-read-aps',
          'dsm-cancel-locked-addendum',
          'dsm-approve-addendum',
          'dsm-rerun-room-replacement-rules',
          'dsm-view-pending-location-rules',
          'dsm-edit-locked-addendum-location'
        ],
        'user-setup': ['read-dsm-user-setup'],
        packages: ['manage-dsm-packages'],
        'appointment-scenarios': ['manage-dsm-appointment-scenarios'],
        reports: ['read-dsm-selection-summary-report', 'read-dsm-commission-report'],
        'location-rules': ['manage-dsm-location-rules'],
        appointments: ['dsm-read-appointments', 'dsm-view-all-appointments']
      },
      'Décor Consultant': {
        'product-catalog': ['read'],
        category: ['read'],
        template: ['read'],
        aps: ['dsm-read-aps', 'dsm-cancel-locked-addendum'],
        appointments: ['dsm-read-appointments', 'dsm-flag-own-appointment']
      },
      'Décor Receptionist': {
        'product-catalog': ['read'],
        category: ['read'],
        template: ['read'],
        aps: ['dsm-read-aps', 'dsm-cancel-locked-addendum'],
        appointments: ['dsm-read-appointments', 'dsm-view-all-appointments', 'dsm-schedule-appointments']
      }
    },
    Administration: {}
  };

  private readonly onRuleChangeSubject = new BehaviorSubject<any>(null);
  onRuleChange$ = this.onRuleChangeSubject.asObservable();

  ability = createMongoAbility<AppAbility>([]);

  /**
   * Maps module display names to their internal type names
   */
  private normalizeModuleName(moduleName: string): string {
    const moduleNameMap: Record<string, string> = {
      'Home Building': 'HomeBuilding',
      HomeBuilding: 'HomeBuilding',
      Scheduler: 'Scheduler',
      Warranty: 'Warranty',
      Sales: 'Sales',
      'File Management': 'File Management',
      DSM: 'DSM'
    };
    return moduleNameMap[moduleName] || moduleName;
  }

  updateAbilityForModule(moduleName: string, role: string) {
    // Normalize the module name to match ModulesTypes
    const normalizedModuleName = this.normalizeModuleName(moduleName);

    // Validate if moduleName is a valid ModulesTypes
    if (!Object.keys(this.rules).includes(normalizedModuleName)) {
      console.error(`Invalid module name: ${moduleName}. Must be one of: ${Object.keys(this.rules).join(', ')}`);
      this.ability.update([]);
      return;
    }

    const typedModuleName = normalizedModuleName as ModulesTypes;

    // Validate if role exists for the given module
    const moduleRoles = Object.keys(this.rules[typedModuleName]);
    if (!moduleRoles.includes(role)) {
      console.error(`Invalid role: ${role} for module ${moduleName}. Must be one of: ${moduleRoles.join(', ')}`);
      this.ability.update([]);
      return;
    }

    const { can, rules } = new AbilityBuilder<AppAbility>(createMongoAbility);
    const permissions = this.rules[typedModuleName][role as keyof (typeof this.rules)[typeof typedModuleName]];

    if (!permissions) {
      console.error(`No permissions found for module ${moduleName} and role ${role}`);
      this.ability.update([]);
      return;
    }
    Object.entries(permissions).forEach(([subject, actions]) => {
      (actions as any[]).forEach(action => {
        can(action as any, subject as any);
      });
    });

    this.ability.update(rules);
    this.onRuleChangeSubject.next({
      module: normalizedModuleName,
      role: role,
      rules: rules
    });
  }
}
